import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import Collapse from "react-bootstrap/Collapse";
import Select from "react-select";
import Button from "../../form-components/Button";
import Input from "../../form-components/Input";
import importAll from "../../chat-component/images";
import { updatePersona } from "../../../features/persona/updatePersonaSlice";
import { createPersona } from "../../../features/persona/createPersonaSlice";
import { getPersona } from "../../../features/persona/getPersonaSlice";
import { showPersona } from "../../../features/persona/showPersonaSlice";
import Loader from "../../../common/loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import Form from 'react-bootstrap/Form';
import RangeSlider from 'react-bootstrap-range-slider';


const images = importAll(
  require.context(
    "../../../assets/images/",
    false,
    /\.(png|jpe?g|svg|GIF|webp)$/
  )
);

const NAME_LENGTH = 20,
  PROMPT_LENGTH = 1500;

export const gptModalOptions = [
  {
    value: "gpt-3.5-turbo",
    label: "GPT-3.5 Turbo",
  },
  {
    value: "gpt-4",
    label: "GPT-4",
  },
];

/**
 * entity options
 */
export const intentOptions = [
  {
    label: "Informational",
    value: "informational",
  },
  {
    label: "Navigational",
    value: "navigational",
  },
  {
    label: "Transactional",
    value: "transactional",
  },
  {
    label: "Commercial",
    value: "commercial",
  },
  {
    label: "Support Agent",
    value: "support_agent",
  },
  {
    label: "None",
    value: "none",
  },
];

export const readabilityOptions = [
  {
    label: "7th Grader",
    value: "7th_grader",
  },
  {
    label: "8th Grader",
    value: "8th_grader",
  },
  {
    label: "9th Grader",
    value: "9th_grader",
  },
  {
    label: "None",
    value: "none",
  },
];

export const generalAccessOptions = [
  {
    label: "Private",
    value: "private",
  },
  {
    label: "Public",
    value: "public",
  },
  // {
  //   label: "Shared with team",
  //   value: "Shared with team",
  // },
];

const emptyPersona = {
  id: "",
  uuid: "",
  name: "",
  prompt: "",
  image_name: "",
  intent: intentOptions[0].value,
  readability: readabilityOptions[0].value,
  general_access: generalAccessOptions[0].value,
  model: gptModalOptions[0].value,
  temperature: "0.2",
  is_default: false,
  created_at: "",
};

const PersonaModal = ({
  isVisible,
  toggleFunction,
  persona,
  modalTitle,
}) => {
  const dispatch = useDispatch();
  const { responseData: personaData } = useSelector(
    (state) => state?.showPersonaReducer
  );
  const { showIsLoading, editIsLoading, createIsLoading } = useSelector(
    (state) => ({
      showIsLoading: state?.showPersonaReducer?.isLoading,
      editIsLoading: state?.updatePersonaReducer?.isLoading,
      createIsLoading: state?.createPersonaReducer?.isLoading,
    })
  );
  const [input, setInput] = useState({ ...emptyPersona });
  const [inputError, setInputError] = useState({});
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleChangeInput = (key, value) => {
    input[key] = value;
    setInput({ ...input });
  };

  const [open, setOpen] = useState(false);

  const save = async () => {
    let error = {};

    if (!input.name.trim()) {
      error.name = "Please enter name";
    } else if (input.name.length > NAME_LENGTH) {
      error.name = `Name should contain less than ${NAME_LENGTH} characters.`;
    }

    if (!input.prompt.trim()) {
      error.prompt = "Please enter prompt";
    } else if (input.prompt.length > PROMPT_LENGTH) {
      error.prompt = `Prompt should contain less than ${PROMPT_LENGTH} characters.`;
    }

    if (!input.intent.trim()) {
      error.intent = "Please select intent";
    }
    if (!input.readability.trim()) {
      error.readability = "Please enter readability";
    }
    if (!input.general_access.trim()) {
      error.general_access = "Please enter general access";
    }

    setInputError({ ...error });

    if (Object.keys(error).length === 0) {
      input.is_default = input.is_default ? 1 : 0;
      const formData = new FormData();
      for (const key in input) {
        if (key === "image_name" && input.image_name && typeof input.image_name !== "string") {
          formData.append(key, input[key], input[key].name);
        }
        else {
          formData.append(key, input[key]);
        }
      }
      if (input.uuid) {
        await dispatch(
          updatePersona({ promptData: formData, uuid: input.uuid })
        );
      } else {
        await dispatch(createPersona(formData));
      }
      toggleFunction();
      await dispatch(getPersona({ perPage: 10, currentPage: 1 }));
    }
  };

  useEffect(() => {
    setInput({ ...emptyPersona });
    setUploadedFile("");
    setInputError({});
    if (isVisible && persona.uuid) {
      dispatch(showPersona(persona.uuid));
    }
  }, [persona]);

  useEffect(() => {
    if (personaData) {
      setInput({
        id: personaData.id ?? emptyPersona.id,
        uuid: personaData.uuid ?? emptyPersona.uuid,
        name: personaData.name ?? emptyPersona.name,
        prompt: personaData.prompt ?? emptyPersona.prompt,
        image_name: personaData.image_name ?? emptyPersona.image_name,
        model: personaData.model ?? emptyPersona.model,
        temperature: personaData.temperature ?? emptyPersona.temperature,
        intent: personaData.intent ?? emptyPersona.intent,
        readability: personaData.readability ?? emptyPersona.readability,
        general_access: personaData.general_access ?? emptyPersona.general_access,
        is_default: personaData.is_default?.toString() === "1",
        created_at: personaData.created_at ?? emptyPersona.created_at,
      });
    }
  }, [personaData]);

  const onUploadImage = (e) => {
    if (e.target.files.length) {
      const image = e.target.files[0];
      if (!image.type.includes("image")) {
        setInputError({
          ...inputError,
          image_name: "Please upload image file",
        });
        return;
      }
      else if (image.size > (100 * 1024)) {
        setInputError({
          ...inputError,
          image_name: "Image should be less than 100kb",
        });
        return;
      }
      setInputError({
        ...inputError,
        image_name: "",
      });
      setInput({ ...input, image_name: image });
      setUploadedFile(URL.createObjectURL(image));
    }
  }

  return (
    <>
      {(showIsLoading || editIsLoading || createIsLoading) && <Loader />}
      <Modal
        data-bs-keyboard="false"
        onHide={toggleFunction}
        show={isVisible}
        className="modal fade addsummary-prompt-modal persona-modal"
        centered={true}
        dialogClassName="common-modal modal-dialog-scrollable"
      >
        <ModalHeader closeButton onHide={toggleFunction}>
          <h3 className="modal-title">{modalTitle}</h3>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-12">
              <div className="form-group seting-btn pe-5 mb-0">
                <label className="form-label">Who is your Persona?</label>
                <button
                  className="p-0 btn"
                  onClick={() => setOpen(!open)}
                  aria-controls="example-collapse-text"
                  aria-expanded={open}
                >
                  <img
                    className="w-100"
                    src={images["setting-2.svg"]}
                    alt="setting-2"
                  />
                </button>
              </div>
              <div className="upload-profile-icn">
                <div className="upload-pf-img">
                  {uploadedFile || input.image_name
                    ? <img className="w-100" src={uploadedFile || input.image_name} alt="userpf" />
                    : <img className="w-100 slect-uplod-img" src={images["userpf.svg"]} alt="userpf" />
                  }
                </div>
                <div>
                  <button type="button" className="btn upload-profile-badge">
                    Upload
                    <Form.Control type="file" onChange={onUploadImage} accept="image/*" />
                    <img  className="export-img-ic ms-1" src={images["exportpficn.svg"]}  alt="exportpficn" />
                  </button>
                  <span className="text-danger">{inputError.image_name}</span>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-6">
              <div className="form-group">
                <label className="form-label">
                  Persona Name*
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip
                        className="persona-name-tooltip cmn-summary-tooltip"
                        id={input.uuid + "-name"}
                      >
                        Name depicts an alias to your Persona Name.
                      </Tooltip>
                    }
                  >
                    <img
                      src={images["info.svg"]}
                      alt=""
                      className="ms-2 cursor-pointer"
                    />
                  </OverlayTrigger>
                </label>
                <Input
                  type="text"
                  placeholder="Personal Assistant Chatbot"
                  className="form-control"
                  value={input.name}
                  onChange={(e) => handleChangeInput("name", e.target.value)}
                  onFocus={() => setIsNameFocused(true)}
                  onBlur={() => setIsNameFocused(false)}
                />
                <span className="text-danger">{inputError.name}</span>
                <span className="chat-form-note position-static float-end">
                  {isNameFocused
                    ? <>{input.name.length}/{NAME_LENGTH}</>
                    : input.name.length
                  }
                  {" "} character{input.name.length > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-6">
              <div className="form-group">
                <label className="form-label">Prompt Intent</label>
                <Select
                  id="inputGroupSelect01"
                  name="entity"
                  options={intentOptions}
                  placeholder="Commercial"
                  classNamePrefix="cmn-control"
                  value={intentOptions.find((opt) => opt.value === input.intent)}
                  onChange={(e) => handleChangeInput("intent", e.value)}
                />
                <span className="text-danger">{inputError.intent}</span>
              </div>
            </div>
            <div className="col-md-12 pt-1">
              <div className="form-group">
                <label className="form-label">
                  Prompt*
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip
                        className="persona-pompt-tl cmn-summary-tooltip"
                        id={input.uuid + "-prompt"}
                      >
                        A 'Prompt' instructs AI. It's your input, like a
                        question or sentence, helping AI understand what you
                        want.
                      </Tooltip>
                    }
                  >
                    <img
                      src={images["info.svg"]}
                      alt=""
                      className="ms-2 cursor-pointer"
                    />
                  </OverlayTrigger>
                </label>
                <textarea
                  placeholder="Imagine you have a personal assistant chatbot. Start a conversation with it, asking it to help you schedule your week, set reminders, and find information online."
                  className="form-control sum-prompt-textarea"
                  value={input.prompt}
                  onChange={(e) => handleChangeInput("prompt", e.target.value)}
                  rows={"3"}
                  style={{ height: "auto" }}
                  onFocus={() => setIsPromptFocused(true)}
                  onBlur={() => setIsPromptFocused(false)}
                />
                <span className="text-danger">{inputError.prompt}</span>
                <span className="chat-form-note position-static float-end">
                  {isPromptFocused
                    ? <>{input.prompt.length}/{PROMPT_LENGTH}</>
                    : input.prompt.length
                  }
                  {" "} character{input.prompt.length > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-6">
              <div className="form-group">
                <label className="form-label">Readability</label>
                <Select
                  id="inputGroupSelect01"
                  name="entity"
                  options={readabilityOptions}
                  placeholder="None"
                  classNamePrefix="cmn-control"
                  menuPlacement="auto"
                  value={readabilityOptions.find((opt) => opt.value === input.readability)}
                  onChange={(e) => handleChangeInput("readability", e.value)}
                />

                <span className="text-danger">{inputError.readability}</span>
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-6">
              <div className="form-group">
                <label className="form-label">General Access</label>
                <Select
                  id="inputGroupSelect01"
                  name="entity"
                  options={generalAccessOptions}
                  placeholder="Private"
                  classNamePrefix="cmn-control"
                  menuPlacement="auto"
                  value={generalAccessOptions.find((opt) => opt.value === input.general_access)}
                  onChange={(e) => handleChangeInput("general_access", e.value)}
                />

                <span className="text-danger">{inputError.general_access}</span>
              </div>
            </div>
          </div>
          <Collapse in={open}>
            <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">Model</label>
                <Select
                  options={gptModalOptions}
                  value={gptModalOptions.find((opt) => opt.value === input.model)}
                  onChange={(e) => handleChangeInput("model", e.value)}
                  isSearchable={false}
                  classNamePrefix="cmn-control"
                  placeholder="GPT-3.5 Turbo"
                  menuPlacement="auto"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="form-label">
                  Temperature
                </label>
                <div className="custom-slider">
                  <span className="cs-slider-no cs-stx-lft">0</span>
                  <RangeSlider
                    value={input.temperature}
                    onChange={e => handleChangeInput("temperature", e.target.value)}
                    min={0}
                    max={1}
                    step={0.1}
                  />
                  <span className="cs-slider-no cs-stx-rgt">1</span>
                </div>
              </div>
            </div>
            </div>
          </Collapse>
        </ModalBody>
        <ModalFooter>
          <div className="form-group mb-0">
            <div className="form-check text-start footer-check">
              <Input
                onChange={(e) => handleChangeInput("is_default", !input.is_default)}
                className="form-check-input"
                type="checkbox"
                id="is_default"
                name="is_default"
                checked={input.is_default}
              />
              <label className="form-check-label" htmlFor="is_default">
                Set as default
              </label>
            </div>
          </div>
          <Button type="button" className="btn btn-primary-md" onClick={save}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PersonaModal;
